import { useEffect, useRef, useState } from "react";

/**
 * 모바일 가상 키보드가 열렸을 때 visual viewport 기준 하단 inset(px)을 반환합니다.
 */
export function useKeyboardInset() {
  const [keyboardInset, setKeyboardInset] = useState(0);
  const maxInnerHeightRef = useRef(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const MIN_KEYBOARD_HEIGHT = 120;
    const viewport = window.visualViewport;
    maxInnerHeightRef.current = window.innerHeight;

    const isEditableElementFocused = () => {
      const activeElement = document.activeElement as HTMLElement | null;
      if (!activeElement) {
        return false;
      }

      const tagName = activeElement.tagName.toLowerCase();
      const isEditableTag = tagName === "input" || tagName === "textarea";
      const isContentEditable = activeElement.isContentEditable;

      return isEditableTag || isContentEditable;
    };

    const updateKeyboardInset = () => {
      const visualViewportInset = viewport
        ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
        : 0;

      const currentInnerHeight = window.innerHeight;
      maxInnerHeightRef.current = Math.max(maxInnerHeightRef.current, currentInnerHeight);

      const resizeBasedInset = Math.max(0, maxInnerHeightRef.current - currentInnerHeight);
      const inferredInset =
        isEditableElementFocused() && resizeBasedInset > MIN_KEYBOARD_HEIGHT ? resizeBasedInset : 0;

      const nextInset = Math.max(visualViewportInset, inferredInset);
      setKeyboardInset((prev) => (Math.abs(prev - nextInset) > 1 ? nextInset : prev));
    };

    const scheduleKeyboardInsetUpdate = () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = requestAnimationFrame(() => {
        updateKeyboardInset();
      });
    };

    const handleOrientationChange = () => {
      maxInnerHeightRef.current = window.innerHeight;
      scheduleKeyboardInsetUpdate();

      window.setTimeout(() => {
        maxInnerHeightRef.current = Math.max(maxInnerHeightRef.current, window.innerHeight);
        scheduleKeyboardInsetUpdate();
      }, 350);
    };

    scheduleKeyboardInsetUpdate();
    window.addEventListener("resize", scheduleKeyboardInsetUpdate);
    window.addEventListener("orientationchange", handleOrientationChange);
    window.addEventListener("focusin", scheduleKeyboardInsetUpdate);
    window.addEventListener("focusout", scheduleKeyboardInsetUpdate);

    if (viewport) {
      viewport.addEventListener("resize", scheduleKeyboardInsetUpdate);
      viewport.addEventListener("scroll", scheduleKeyboardInsetUpdate);
    }

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      window.removeEventListener("resize", scheduleKeyboardInsetUpdate);
      window.removeEventListener("orientationchange", handleOrientationChange);
      window.removeEventListener("focusin", scheduleKeyboardInsetUpdate);
      window.removeEventListener("focusout", scheduleKeyboardInsetUpdate);

      if (viewport) {
        viewport.removeEventListener("resize", scheduleKeyboardInsetUpdate);
        viewport.removeEventListener("scroll", scheduleKeyboardInsetUpdate);
      }
    };
  }, []);

  return keyboardInset;
}
